import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { JobType } from "../../types/resume";

interface JobTypesResponse {
  data: JobType[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useJobTypes = () => {
  return useQuery({
    queryKey: ["jobTypes"],
    queryFn: async (): Promise<JobType[]> => {
      const response = await USERAXIOS.get<JobTypesResponse>("/job_types");
      return response.data.data;
    },
  });
};

export const useJobTypesInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["jobTypes", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<JobTypesResponse> => {
      const response = await USERAXIOS.get<JobTypesResponse>(`/job_types`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

export const useJobTypesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useJobTypesInfinite(pageSize, searchTerm);

  // Flatten all pages into a single array
  const jobTypes = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: jobTypes,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
